import {extractParenthesisContents} from "./stringUtils";

/**
 * The byte-size threshold at which file size display switches from KB to MB in `getAttachmentFileSizeString`.
 */
export const maxSizeInBytesToDisplayAsKB = 1023000;

/**
 * Checks if a filename has a video extension
 */
export const isVideoFileByName = (name: string) => /\.(mp4|mov|avi|mkv|webm)$/i.test(name);

/**
 * Formats a date as a filename-safe timestamp string (YYYYMMDD_HHMMSS)
 */
export const formatDateForFilename = (date = new Date()) => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

/**
 * Strips the group name (including surrounding parentheses) from an input string to extract the clean document type name.
 */
export const processDocTypeString = (input:any, groupName:any) => {
    const group = extractParenthesisContents(groupName);
    const substring = input.replace(group, "")
    const name = substring.trim().replace(/^-+|-+$/g, "").trim();
    return name;
}

const officeMimeTypes = [
    'application/msword', //doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', //docx
    'application/vnd.ms-excel', //xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', //xlsx
    'application/vnd.ms-powerpoint', //ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation' //pptx
    // Add more mappings as needed
];

/**
 * Checks whether a MIME type string matches one of the known Microsoft Office document types.
 *
 * @param mimeType - A MIME type string to check against known Microsoft Office types.
 * @returns true if mimeType matches one of the known Office MIME types (doc, docx, xls, xlsx, ppt, pptx).
 */
export const isMicrosoftType = (mimeType: string) => {
    return officeMimeTypes.includes(mimeType);
};

/**
 * Decodes a base64-encoded PDF string into a Blob and returns a blob URL for preview or download.
 *
 * @param base64PDF - A base64-encoded PDF string (no data URI prefix — raw base64 characters only).
 * @returns A blob: URL pointing to the decoded PDF content.
 */
export const createPdfUrl = (base64PDF: string) => {
    const pdfData = atob(base64PDF);
    const dataArray = new Uint8Array(pdfData.length);
    for (let i = 0; i < pdfData.length; i++) {
        dataArray[i] = pdfData.charCodeAt(i);
    }
    const blob = new Blob([dataArray], { type: 'application/pdf' });
    const pdfUrl = URL.createObjectURL(blob);
    return pdfUrl;
}

/**
 * Reads a File object using FileReader and returns a Promise resolving to its content as a base64 string (without the data URI prefix).
 *
 * @param file - A browser File object to read.
 * @returns A Promise that resolves to the file's base64-encoded content without the data URI prefix (i.e. the characters after the comma in `data:...;base64,ABC...`).
 */
export const getFileBase64 = (file: any): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const [, ...base64] = (reader.result as string).split(',');
            resolve(base64.join());
        };
        reader.onerror = (error) => reject(error);
    });

/**
 * Reads a Blob asynchronously via FileReader and returns a Promise resolving to a full data URI (base64-encoded) representing the blob's contents.
 *
 * @param blob - A Blob to convert into a data URI.
 * @returns A Promise resolving to a full data URI string (e.g. `data:application/pdf;base64,ABC...`). On read error, rejects with an Error; on unexpected exceptions, silently returns undefined.
 */
export const generateDataURI = async (blob: Blob): Promise<any> => {
    try {
        const reader = new FileReader();
        return new Promise<string>((resolve, reject) => {
            reader.onload = () => {
                const dataURI = reader.result as string;
                resolve(dataURI);
            };
            reader.onerror = () => {
                reject(new Error('Error reading the file.'));
            };
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error('Error generating Data URI:', error);
    }
};

/**
 * Divides the given byte count by 1024 and returns the result as a floating-point kilobyte value.
 *
 * @returns The value divided by 1024, as a floating-point number.
 */
export const convertBytesToKilobytes = (bytes: number) => bytes / 1024;

/**
 * Divides the given byte count by 1,048,576 (1024 × 1024) and returns the result as a floating-point megabyte value.
 */
export const convertBytesToMegabytes = (bytes: number) => bytes / 1024 / 1024;

/**
 * Formats a file size in bytes as a human-readable string with units. Threshold is 1,023,000 bytes: below that shows KB, above shows MB.
 *
 * @param fileSize - A file size in bytes.
 * @returns A string like \"2.50 KB\" or \"1.23 MB\" — always with 2 decimal places and an appropriate suffix.
 */
export const getAttachmentFileSizeString = (fileSize: number) => {
    const size =
        fileSize > maxSizeInBytesToDisplayAsKB
            ? `${convertBytesToMegabytes(fileSize).toFixed(2)} MB`
            : `${convertBytesToKilobytes(fileSize).toFixed(2)} KB`;
    return size;
};
